import React, { useState } from 'react';
import { Box, Stack, Button, TextField, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormikContext, getIn } from 'formik';
import EmpModalContainer from '../../../containers/common/form/modal/EmpModalContainer';
import NumberFormatText from '../../common/form/NumberFormatText';
import FormLabel from '../../common/form/FormLabel';

const RoInternalCostsForm = ({
  cost,
  index,
  touched,
  errors,
  handleRemove,
  handleChange,
  handleBlur,
  totalSales,
  handleRoAdj,
}) => {
  const formikProps = useFormikContext();

  const [openEmp, setOpenEmp] = useState(false);
  const [title, setTitle] = useState('사원찾기');

  const roPartnerUsrNm = `roInternalCosts[${index}].roPartnerUsrNm`;
  const touchedRoPartnerUsrNm = getIn(touched, roPartnerUsrNm);
  const errorRoPartnerUsrNm = getIn(errors, roPartnerUsrNm);

  const roPartnerUsr = `roInternalCosts[${index}].roPartnerUsr`;

  const roInternalAmt = `roInternalCosts[${index}].roInternalAmt`;
  const touchedRoInternalAmt = getIn(touched, roInternalAmt);
  const errorRoInternalAmt = getIn(errors, roInternalAmt);

  const roInternalAmtPercent = `roInternalCosts[${index}].roInternalAmtPercent`;
  const touchedRoInternalAmtPercent = getIn(touched, roInternalAmtPercent);
  const errorRoInternalAmtPercent = getIn(errors, roInternalAmtPercent);

  // 부모 정산 기준 금액
  const roProfitAmt = Number(formikProps.values?.roProfitAmt) || 0;

  // 💡 [금액 창 Blur] 입력이 끝났을 때 비로소 퍼센트를 계산함 (타이핑 방해 금지)
  const handleAmtBlur = () => {
    const currentAmt = Number(formikProps.values.roInternalCosts[index].roInternalAmt) || 0;

    if (roProfitAmt > 0) {
      // 💡 원래 기획대로 소수점 없이 정수로만 딱 떨어지게 반올림합니다.
      const calculatedPercent = Math.round((currentAmt / roProfitAmt) * 100);
      formikProps.setFieldValue(roInternalAmtPercent, calculatedPercent, false);
    } else {
      formikProps.setFieldValue(roInternalAmtPercent, 0, false);
    }

    // 최종 부모 정산 함수 호출
    handleRoAdj();
  };

  // 💡 [퍼센트 창 Blur] 입력이 끝났을 때 비로소 금액을 계산함
  const handlePercentBlur = () => {
    const currentPercent = Number(formikProps.values.roInternalCosts[index].roInternalAmtPercent) || 0;

    if (roProfitAmt > 0) {
      const calculatedAmt = Math.round(roProfitAmt * (currentPercent / 100));
      formikProps.setFieldValue(roInternalAmt, calculatedAmt, false);
    } else {
      formikProps.setFieldValue(roInternalAmt, 0, false);
    }

    // 최종 부모 정산 함수 호출
    handleRoAdj();
  };

  const openEmpModal = (type, title) => {
    setTitle(title);
    setOpenEmp(true);
  };
  const handleEmpClose = () => {
    setOpenEmp(false);
  };

  const selectedEmployee = (emp) => {
    formikProps.setFieldValue(roPartnerUsrNm, emp.usrNm, true);
    formikProps.setFieldValue(roPartnerUsr, emp.usrUuid, true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        p: 1,
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: '180px' }}>
          <FormLabel textAlign="center">협업본부 영업담당</FormLabel>
        </Box>
        <Box>
          <TextField
            size="small"
            required={true}
            variant="outlined"
            disabled
            fullWidth
            id={roPartnerUsrNm}
            name={roPartnerUsrNm}
            value={formikProps.values.roInternalCosts[index].roPartnerUsrNm || ''}
            error={touchedRoPartnerUsrNm && Boolean(errorRoPartnerUsrNm)}
            helperText={touchedRoPartnerUsrNm && errorRoPartnerUsrNm}
          />
        </Box>
        <Box>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={() => openEmpModal('', '협업본부 영업담당 선택')}
          >
            협업본부 영업담당 선택
          </Button>
        </Box>
        <Box>
          <TextField
            size="small"
            required
            variant="outlined"
            fullWidth
            id={roInternalAmt}
            name={roInternalAmt}
            value={formikProps.values.roInternalCosts[index].roInternalAmt ?? 0}
            // 💡 입력할 때는 Formik 기본 작동(handleChange)에만 맡겨 타이핑이 씹히지 않게 함
            onChange={formikProps.handleChange}
            // 💡 포커스가 빠져나갈 때 한 번만 계산 프로세스 작동
            onBlur={handleAmtBlur}
            error={touchedRoInternalAmt && Boolean(errorRoInternalAmt)}
            helperText={touchedRoInternalAmt && errorRoInternalAmt}
            InputProps={{
              inputComponent: NumberFormatText,
            }}
          />
        </Box>
        <Box>
          <TextField
            size="small"
            required
            variant="outlined"
            fullWidth
            id={roInternalAmtPercent}
            name={roInternalAmtPercent}
            value={formikProps.values.roInternalCosts[index].roInternalAmtPercent ?? 0}
            // 💡 입력할 때는 Formik 기본 작동(handleChange)에만 맡김
            onChange={formikProps.handleChange}
            // 💡 포커스가 빠져나갈 때 한 번만 계산 프로세스 작동
            onBlur={handlePercentBlur}
            error={touchedRoInternalAmtPercent && Boolean(errorRoInternalAmtPercent)}
            helperText={touchedRoInternalAmtPercent && errorRoInternalAmtPercent}
            InputProps={{
              inputComponent: NumberFormatText,
            }}
            sx={{ width: 80 }}
          />
        </Box>
        <Box>%</Box>
        <Box>
          <IconButton color="error" aria-label="delete" onClick={handleRemove}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Stack>
      {openEmp && (
        <EmpModalContainer
          title={title}
          multi={false}
          open={openEmp}
          handleClose={handleEmpClose}
          selectedEmployee={selectedEmployee}
        />
      )}
    </Box>
  );
};

export default RoInternalCostsForm;
